import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiShield, FiSunrise, FiUsers, FiTrendingUp } from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

const ValuesSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    gsap.from(section.querySelector("h2"), {
      opacity: 0,
      y: 50,
      duration: 1,
      ease: "power3.out",
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    const cards = gsap.utils.toArray(".value-card");
    gsap.from(cards, {
      opacity: 0,
      y: 50,
      scale: 0.9,
      duration: 0.7,
      ease: "power3.out",
      stagger: 0.2,
      scrollTrigger: {
        trigger: section.querySelector(".values-grid"),
        start: "top 85%",
        toggleActions: "play none none none",
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const values = [
    {
      icon: <FiShield className="h-10 w-10 text-nature-green-dark" />,
      title: "Respect",
      description:
        "Honoring our elders, our neighbors, and the land that sustains us.",
    },
    {
      icon: <FiUsers className="h-10 w-10 text-nature-green-dark" />,
      title: "Unity",
      description: "Standing together as one family, we are unbreakable.",
    },
    {
      icon: <FiSunrise className="h-10 w-10 text-nature-green-dark" />,
      title: "Integrity",
      description: "Living truthfully and with honor in all that we do.",
    },
    {
      icon: <FiTrendingUp className="h-10 w-10 text-nature-green-dark" />,
      title: "Progress",
      description: "Building a brighter future while cherishing our past.",
    },
  ];

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-6 text-center">
        <h2 className="mb-16 font-serif text-4xl font-semibold tracking-wide text-nature-green-dark md:text-5xl">
          The Principles That Guide Us
        </h2>
        <div className="values-grid grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((value, index) => (
            <div
              key={index}
              className="value-card rounded-2xl border border-stone-200/70 bg-stone-50 p-8 shadow-sm transition-shadow duration-300 hover:shadow-lg"
            >
              <div className="mb-5 flex justify-center">{value.icon}</div>
              <h3 className="mb-3 text-2xl font-bold text-gray-800">
                {value.title}
              </h3>
              <p className="text-lg leading-relaxed text-gray-600">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;
